import React, { useState, useEffect } from "react";
import {
    Button,
    Container,
    Typography,
    List,
    ListItem,
    ListItemText,
    Box,
    Card,
    CardContent,
    CardActions,
    Grid,
    TextField,
    Paper,
    Modal,
    Pagination,
} from "@mui/material";
import AddTopicModal from "../components/AddTopicModal";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
});

const Forum = () => {
    const [topics, setTopics] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isAddTopicOpen, setIsAddTopicOpen] = useState(false);
    const [isUnauthorizedModalOpen, setIsUnauthorizedModalOpen] = useState(false);
    const [unauthorizedMessage, setUnauthorizedMessage] = useState("");
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const fetchTopics = async (page = 1, search = "") => {
        try {
            const response = await api.get("/api/forum/topics", {
                params: { page, limit: 10, search },
            });
            setTopics(response.data.topics);
            setTotalPages(response.data.totalPages);

            // Arama sonuçlarını ayır
            if (search) {
                setSearchResults(response.data.searchResults);
            } else {
                setSearchResults([]);
            }
        } catch (error) {
            
        }
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            setIsLoggedIn(true);
        }
    }, []);

    const handleSearch = (e) => {

        if (!isLoggedIn) {
            setUnauthorizedMessage(t("login_required_search"));
            setIsUnauthorizedModalOpen(true);
            return;
        }
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        fetchTopics(1, term); // Arama sırasında sayfayı sıfırla
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        fetchTopics(value, searchTerm); // Sayfa değiştiğinde arama terimini gönder
    };

    const handleAddTopicClick = async () => {

        const token = localStorage.getItem("token"); // Token'ı al
        if (!token) {
            // Eğer token yoksa, doğrudan uyarı göster
            setUnauthorizedMessage(t("login_required_comment"));
            setIsUnauthorizedModalOpen(true);
            return;
        }

        const isValid = await validateToken();
        if (!isValid) {
            setIsUnauthorizedModalOpen(true); // Geçersiz token için modal aç
            return;
        }
        setIsAddTopicOpen(true); // Token geçerliyse modal aç
    };

    const handleCloseUnauthorizedModal = () => {
        setIsUnauthorizedModalOpen(false);
    };



    const validateToken = async () => {
        try {
            const response = await api.get("/api/auth/validate-token", {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            return response.data.valid; // Token geçerliyse true döner
        } catch (error) {
            
            return false; // Token geçersizse false döner
        }
    };

    useEffect(() => {
        fetchTopics();
    }, []);

    return (
        <Container maxWidth="lg" style={{
            marginTop: "20px",
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            padding: "20px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        }}>
            <Grid container spacing={4}>
                {/* Sol Bölüm: Forum Başlıkları */}
                <Grid item xs={12} md={8}>
                    <Typography variant="h1" style={{ textAlign: "left", marginBottom: "20px", fontWeight: "bold" }}>
                        {t("forum_topics")}
                    </Typography>
                    <Box display="flex" justifyContent="flex-end" marginBottom="20px">
                        <Button variant="contained" color="primary" onClick={handleAddTopicClick}>
                            {t("add_new_topic")}
                        </Button>
                    </Box>
                    <Box>
                        {topics.length === 0 ? (
                            <Typography variant="h6" style={{ textAlign: "center", color: "gray", marginTop: "50px" }}>
                                {t("no_topics_available")}
                            </Typography>
                        ) : (
                            <Grid container spacing={3}>
                                {topics.map((topic) => (
                                    <Grid item xs={12} key={topic.id}>
                                        <Card style={{
                                            borderRadius: "15px",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        }}>
                                            <CardContent>
                                                <Typography variant="h5" style={{ fontWeight: "bold", color: "#3f51b5", marginBottom: "10px" }}>
                                                    {topic.title}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" style={{ marginBottom: "15px" }}>
                                                    {t("created_by")}: {topic.author.name} | {t("created_on")}: {new Date(topic.createdAt).toLocaleDateString()}
                                                </Typography>
                                                <Typography variant="body2" style={{ marginTop: "10px", lineHeight: "1.5", fontStyle: "italic" }}
                                                    dangerouslySetInnerHTML={{ __html: topic.content.slice(0, 100) + "..." }} />
                                            </CardContent>
                                            <CardActions>
                                                <Button size="small" variant="outlined" color="primary" onClick={() =>
                                                    navigate(`/topic/${topic.id}-${topic.title.toLowerCase().replace(/\s+/g, '-')}`)
                                                }>
                                                    {t("view_details")}
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                    <Box display="flex" justifyContent="center" marginTop="20px">
                        <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
                    </Box>
                </Grid>
        
                {/* Sağ Bölüm: Arama ve Sonuçlar */}
                <Grid item xs={12} md={4}>
                    <Paper style={{
                        padding: "20px",
                        borderRadius: "10px",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    }}>
                        <Typography variant="h6" style={{ marginBottom: "20px", fontWeight: "bold" }}>
                            {t("search_topics")}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder={t("search_placeholder")}
                            variant="outlined"
                            value={searchTerm}
                            onChange={handleSearch}
                            disabled={!isLoggedIn}
                            style={{ marginBottom: "20px" }}
                        />
                        {!isLoggedIn && (
                            <Typography variant="body2" style={{ fontStyle: "italic", color: "gray", marginBottom: "10px" }}>
                                {t("login_required_search")}
                            </Typography>
                        )}
                        <Typography variant="body1" style={{ fontWeight: "bold", marginBottom: "10px" }}>
                            {t("search_results")}
                        </Typography>
                        <List>
                            {searchResults.map((topic) => (
                                <ListItem key={topic.id} button onClick={() => navigate(`/topic/${topic.id}`)}>
                                    <ListItemText primary={topic.title} secondary={`${t("created_by")}: ${topic.author.username}`} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        
            {/* Yetkilendirme Gerekli Modalı */}
            <Modal open={isUnauthorizedModalOpen} onClose={handleCloseUnauthorizedModal}
                aria-labelledby="unauthorized-modal-title" aria-describedby="unauthorized-modal-description">
                <Paper style={{
                    width: "400px",
                    margin: "auto",
                    marginTop: "20vh",
                    padding: "20px",
                    textAlign: "center",
                    borderRadius: "10px",
                }}>
                    <Typography id="unauthorized-modal-title" variant="h6" style={{ marginBottom: "20px", fontWeight: "bold", color: "#d32f2f" }}>
                        {t("login_required")}
                    </Typography>
                    <Typography id="unauthorized-modal-description" style={{ marginBottom: "20px" }}>
                        {t("unauthorized_message")}
                    </Typography>
                    <Button variant="contained" color="primary" onClick={() => navigate("/login")} style={{ marginRight: "10px" }}>
                        {t("log_in")}
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => navigate("/register")}>
                        {t("sign_up")}
                    </Button>
                </Paper>
            </Modal>
             {/* Yeni Başlık Modalı */}
        <AddTopicModal
        open={isAddTopicOpen}
        onClose={() => setIsAddTopicOpen(false)}
        refreshTopics={() => fetchTopics(currentPage, searchTerm)}
    />
        </Container>

       
    );
};

export default Forum;
